import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStudio } from "./StudioContext";
import api from "@/lib/api";
import { StudioPage, Panel, SectionLabel, Metric, DrillDownMetric, AwaitingMetric, Badge, DataTable, Drawer, DBlock, DList, DKV, ConfBar, Loading, cx, isSdk } from "./StudioShared";
import SystemStatus from "./SystemStatus";
import ActivityTimeline from "@/components/ActivityTimeline";
import {
  BrainCircuit, Server, Users, BookOpen, Target, Gauge, ArrowUpRight,
  TriangleAlert, CheckCircle2, Sparkles, Info, Lightbulb, ShieldAlert, Globe, FileText,
  Award, Rocket, Archive, ShieldCheck, Building2,
} from "lucide-react";

const STAGE_TONE = { draft: "slate", refined: "amber", in_review: "amber", certified: "green", production: "gold" };
const STAGE_LABEL = { draft: "Draft", refined: "Refined", in_review: "In Review", certified: "Certified", production: "Production" };

const isToday = (iso) => { if (!iso) return false; const d = new Date(iso), n = new Date(); return d.toDateString() === n.toDateString(); };
const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());

export default function StudioDashboard() {
  const { strategists, knowledge, missions, analytics, providers, health } = useStudio();
  const navigate = useNavigate();
  const [planOpen, setPlanOpen] = useState(null);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => { api.get("/admin/mi/workforce-readiness").then((r) => setReadiness(r.data)).catch(() => {}); }, []);

  // Hooks must run unconditionally (before the readiness early-return).
  const confTrend = useMemo(() => {
    const byDay = {};
    (missions || []).forEach((m) => {
      const c = m.document?.confidence;
      if (c == null || !m.created_at) return;
      const key = new Date(m.created_at).toISOString().slice(0, 10);
      (byDay[key] = byDay[key] || []).push(c);
    });
    return Object.keys(byDay).sort().slice(-30)
      .map((k) => ({ day: k, avg: byDay[k].reduce((a, b) => a + b, 0) / byDay[k].length, n: byDay[k].length }));
  }, [missions]);
  const latency = useMemo(() => {
    const withMs = (providers?.providers || []).filter((p) => p.available && p.response_time_ms != null)
      .sort((a, b) => a.response_time_ms - b.response_time_ms);
    if (!withMs.length) return null;
    return { fastest: withMs[0], slowest: withMs[withMs.length - 1], avg: providers?.avg_response_time_ms };
  }, [providers]);
  const planStatus = useMemo(() => {
    const s = { Draft: 0, Running: 0, Completed: 0, Archived: 0 };
    (missions || []).forEach((m) => {
      const st = m.lifecycle_status === "archived" ? "Archived"
        : m.status === "running" ? "Running"
        : m.document ? "Completed" : "Draft";
      s[st] = (s[st] || 0) + 1;
    });
    return s;
  }, [missions]);

  const ready = strategists && knowledge && missions && providers;
  if (!ready) return <StudioPage title="Studio Dashboard" question="How healthy is the intelligence platform today?"><Loading /></StudioPage>;

  const mi = analytics?.mission_intelligence || {};
  const providerList = providers?.providers || [];
  const activeProviders = providerList.filter((p) => p.available);
  const providerHealthy = activeProviders.length > 0 && health?.enabled;
  const plansToday = (missions || []).filter((m) => isToday(m.created_at)).length;
  const topIndustry = Object.keys(mi.plans_by_industry || {})[0];
  const topStrategist = Object.keys(analytics?.strategists?.most_consulted || {})[0];
  const fastest = [...activeProviders].filter((p) => p.response_time_ms != null).sort((a, b) => a.response_time_ms - b.response_time_ms)[0] || activeProviders[0];

  // System alerts — categorized (Information · Warning · Critical · Opportunity). Read-only.
  const alerts = [];
  providerList.forEach((p) => {
    if (p.available && p.success_rate != null && p.success_rate < 0.5)
      alerts.push({ cat: "Critical", text: `${titleCase(p.name)} success rate is critically low (${Math.round(p.success_rate * 100)}%) — provider may be failing.` });
    else if (p.calls > 0 && p.success_rate != null && p.success_rate < 0.9)
      alerts.push({ cat: "Warning", text: `${titleCase(p.name)} success rate is ${Math.round(p.success_rate * 100)}% — investigate provider errors.` });
  });
  if (activeProviders.length === 0) alerts.push({ cat: "Critical", text: "No reasoning provider is active — AI generation will fall back or fail. Add an API key." });
  else if (activeProviders.length === 1) alerts.push({ cat: "Information", text: "Only 1 reasoning provider active — add another API key to enable live failover." });
  const eventsEmpty = !analytics?.business?.events || Object.keys(analytics.business.events).length === 0;
  if (eventsEmpty) alerts.push({ cat: "Information", text: "Conversion analytics (acceptance / proposal funnel) await live customer traffic." });
  const miRate = analytics?.business?.mi_enhanced_proposal_rate;
  if (miRate != null && miRate > 0 && miRate < 1)
    alerts.push({ cat: "Opportunity", text: `${Math.round(miRate * 100)}% of proposals are Mission-Intelligence-enhanced — enhance the rest to lift win rate.` });

  const brief = [
    ["Status", providerHealthy ? "Healthy" : "Degraded", providerHealthy ? "text-emerald-400" : "text-amber-400"],
    ["Mission Plans Today", plansToday, "text-white"],
    ["Average Confidence", mi.avg_confidence != null ? `${Math.round(mi.avg_confidence * 100)}%` : "—", "text-white"],
    ["Top Industry", topIndustry ? titleCase(topIndustry) : "—", "text-white"],
    ["Top Strategist", topStrategist || "—", "text-white"],
    ["Fastest Provider", fastest ? titleCase(fastest.name) : "—", "text-white"],
    ["Outstanding Alerts", alerts.length, alerts.length ? "text-amber-400" : "text-emerald-400"],
  ];

  const recent = (missions || []).slice(0, 6);
  const tiles = [
    { label: "Strategists", icon: Users, to: "/admin/studio/strategists" },
    { label: "Knowledge", icon: BookOpen, to: "/admin/studio/knowledge" },
    { label: "Analytics", icon: Gauge, to: "/admin/studio/analytics" },
    { label: "Providers", icon: Server, to: "/admin/studio/providers" },
  ];

  return (
    <StudioPage title="Studio Dashboard" question="How healthy is the intelligence platform today?" testid="studio-dashboard">
      {/* Executive Brief */}
      <Panel className="p-6 mb-6" testid="executive-brief">
        <div className="flex items-center gap-2 mb-5">
          <Sparkles size={16} className="text-gold" />
          <h2 className="font-heading text-lg text-white">Today's Executive Brief</h2>
          <span className="text-xs text-slate-500 ml-auto">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 divide-x divide-white/5">
          {brief.map(([label, value, color]) => (
            <div key={label} className="px-4 first:pl-0 py-1">
              <SectionLabel className="mb-2">{label}</SectionLabel>
              <div className={cx("font-mono text-2xl font-light leading-none", color)}>{value}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* System Status (operational visibility — consumes /api/health) */}
      <SystemStatus />

      {/* Cockpit metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="cockpit-metrics">
        <Metric testid="metric-mi-health" label="Mission Intelligence" value={health?.enabled ? "Online" : "Offline"} sub={`engine · ${health?.default_model || "—"}`} status={health?.enabled ? "healthy" : "error"} />
        <DrillDownMetric testid="metric-provider-health" label="Provider Health" value={`${activeProviders.length}/${providerList.length}`} sub={`active · ${providers?.routing_policy || "—"}`} status={providerHealthy ? "healthy" : "warning"}
          drillTitle="Provider Health" drillSubtitle={`Failover: ${providers?.failover_status || "—"}`}
          drill={() => (
            <>
              <DataTable testid="provider-health-table" empty="No providers configured." rows={providerList}
                columns={[
                  { key: "name", label: "Provider", render: (p) => <span className="text-white capitalize">{titleCase(p.name)}</span> },
                  { key: "health", label: "Health", render: (p) => <Badge tone={p.connected ? "green" : p.available ? "amber" : "slate"}>{p.health || "unknown"}</Badge> },
                  { key: "lat", label: "Latency", render: (p) => p.response_time_ms != null ? `${p.response_time_ms}ms` : "—" },
                  { key: "sr", label: "Success", render: (p) => p.success_rate != null ? `${Math.round(p.success_rate * 100)}%` : "—" },
                  { key: "calls", label: "API Usage", render: (p) => p.calls ?? 0 },
                ]} />
              <div className="text-[11px] text-slate-500 border border-dashed border-white/10 rounded-sm px-3 py-2">Recent errors &amp; failover history are not yet retained per provider — available after production usage.</div>
            </>
          )} />
        <Metric testid="metric-strategists" label="Active Strategists" value={strategists.length} sub={`${strategists.filter(isSdk).length} SDK · ${strategists.filter((s) => !isSdk(s)).length} legacy`} icon={Users} />
        <Metric testid="metric-knowledge" label="Knowledge Entries" value={(knowledge?.entries || []).length} sub={`${(knowledge?.domains || []).length} domains`} icon={BookOpen} />
        <DrillDownMetric testid="metric-missions" label="Mission Plans" value={missions.length} sub={`${planStatus.Completed} completed · ${planStatus.Draft} draft`} icon={Target}
          drillTitle="Mission Plans — Lifecycle Status"
          drill={() => (
            <>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(planStatus).map(([k, v]) => (
                  <div key={k} className="border border-white/10 rounded-sm p-3 text-center"><div className="font-mono text-2xl text-white">{v}</div><div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{k}</div></div>
                ))}
              </div>
              <DataTable testid="plan-list-table" empty="No mission plans yet." rows={(missions || []).slice(0, 20)} onRowClick={(m) => setPlanOpen(m)} rowTestid={(m) => `plan-row-${m.id}`}
                columns={[
                  { key: "org", label: "Organization", render: (m) => <span className="text-white">{m.org_name || "—"}</span> },
                  { key: "ver", label: "Ver", render: (m) => `v${m.version}` },
                  { key: "conf", label: "Confidence", render: (m) => <ConfBar value={m.document?.confidence || 0} /> },
                ]} />
              <div className="text-[11px] text-slate-500">Click any plan to open its Mission Timeline.</div>
            </>
          )} />
        <DrillDownMetric testid="metric-confidence" label="Avg Confidence" value={mi.avg_confidence != null ? `${Math.round(mi.avg_confidence * 100)}%` : "—"} sub={`${(mi.avg_strategists_consulted || 0).toFixed(1)} strategists/plan`} icon={Gauge}
          drillTitle="Confidence Trend — Last 30 Days"
          drill={() => confTrend.length < 2 ? (
            <div className="text-slate-500 text-sm">Not enough mission history to plot a trend yet. Generate more mission plans to populate this.</div>
          ) : (
            <div className="space-y-1.5" data-testid="confidence-trend">
              {confTrend.map((d, i) => {
                const prev = i > 0 ? confTrend[i - 1].avg : d.avg;
                const drop = d.avg < prev - 0.1;
                return (
                  <div key={d.day} className="flex items-center gap-3 text-xs">
                    <span className="text-slate-500 w-20 shrink-0">{d.day.slice(5)}</span>
                    <div className="flex-1 h-2 bg-white/5 rounded-sm overflow-hidden"><div className={cx("h-full", drop ? "bg-red-400" : "bg-gold")} style={{ width: `${Math.round(d.avg * 100)}%` }} /></div>
                    <span className={cx("font-mono w-10 text-right", drop ? "text-red-400" : "text-slate-300")}>{Math.round(d.avg * 100)}%</span>
                    {drop && <Badge tone="red">drop</Badge>}
                  </div>
                );
              })}
              <div className="text-[11px] text-slate-500 pt-1">Red bars flag a &gt;10-point drop from the prior active day.</div>
            </div>
          )} />
        <Metric testid="metric-mi-rate" label="MI-Enhanced Rate" value={`${Math.round((analytics?.business?.mi_enhanced_proposal_rate || 0) * 100)}%`} sub={`${analytics?.business?.proposals_mi_enhanced || 0}/${analytics?.business?.proposals_total || 0} proposals`} icon={BrainCircuit} />
        <DrillDownMetric testid="metric-latency" label="Avg Latency" value={providers?.avg_response_time_ms != null ? `${providers.avg_response_time_ms}ms` : "—"} sub={`${providers?.total_requests_sampled || 0} requests`} icon={Gauge}
          drillTitle="Provider Latency"
          drill={() => !latency ? (
            <div className="text-slate-500 text-sm">Awaiting live data — no provider response times sampled yet.</div>
          ) : (
            <div className="space-y-3">
              <DKV label="Fastest" value={`${titleCase(latency.fastest.name)} · ${latency.fastest.response_time_ms}ms`} />
              <DKV label="Average" value={latency.avg != null ? `${latency.avg}ms` : "—"} />
              <DKV label="Slowest" value={`${titleCase(latency.slowest.name)} · ${latency.slowest.response_time_ms}ms`} />
            </div>
          )} />
      </div>

      {/* Workforce Readiness — governance view of the strategist roster */}
      {readiness && (
        <div className="mb-6" data-testid="workforce-readiness">
          <div className="flex items-center gap-2 mb-2">
            <SectionLabel>Workforce Readiness</SectionLabel>
            <button onClick={() => navigate("/admin/studio/strategist-builder")} className="text-slate-600 hover:text-gold text-xs inline-flex items-center gap-1 transition-colors ml-auto">Manage roster <ArrowUpRight size={12} /></button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-4" data-testid="readiness-totals">
            <Metric testid="readiness-system-live" label="Live (System)" value={readiness.totals.system_live} sub="in the engine" icon={ShieldCheck} status="healthy" />
            <Metric testid="readiness-production" label="In Production" value={readiness.totals.in_production} sub="custom · deployable" icon={Rocket} />
            <Metric testid="readiness-certified" label="Certified" value={readiness.totals.certified} sub="meet quality bar" icon={Award} />
            <Metric testid="readiness-mission-ready" label="Mission-Ready" value={readiness.totals.mission_ready} sub="certified + production" icon={CheckCircle2} />
            <Metric testid="readiness-inprogress" label="In Progress" value={readiness.totals.in_progress} sub="refined / in review" icon={Gauge} />
            <Metric testid="readiness-archived" label="Archived" value={readiness.totals.archived} sub="retained, not live" icon={Archive} />
          </div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="readiness-workforces">
            {readiness.workforces.map((w) => {
              const readyPct = w.active > 0 ? Math.round((w.mission_ready / w.active) * 100) : 0;
              const isWebsite = w.id === "website";
              return (
                <Panel key={w.id} className="p-5" data-testid={`readiness-wf-${w.id}`}>
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-gold" />
                      <h3 className="font-heading text-white text-base">{w.name}</h3>
                    </div>
                    <Badge tone={isWebsite ? "gold" : "slate"}>{isWebsite ? "live engine" : "not wired yet"}</Badge>
                  </div>
                  <p className="text-slate-500 text-xs mb-3 leading-snug">{w.mission}</p>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="font-mono text-2xl text-white leading-none">{w.mission_ready}<span className="text-slate-600 text-base">/{w.active}</span></div>
                    <div className="text-slate-500 text-xs">mission-ready</div>
                    <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden max-w-[160px]"><div className="h-full bg-gold" style={{ width: `${readyPct}%` }} /></div>
                    <span className="text-gold font-mono text-xs">{readyPct}%</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {readiness.stages.map((s) => w.stages[s] > 0 && (
                      <Badge key={s} tone={STAGE_TONE[s]} testid={`readiness-${w.id}-${s}`}>{STAGE_LABEL[s]} {w.stages[s]}</Badge>
                    ))}
                    {isWebsite && <Badge tone="gold">+{readiness.system.count} System (live)</Badge>}
                    {w.archived > 0 && <Badge tone="red">Archived {w.archived}</Badge>}
                  </div>
                  <div className="text-[11px] text-slate-500">Missions: {(w.eligible_mission_types || []).join(", ") || "—"}</div>
                </Panel>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5"><ShieldCheck size={12} className="text-gold" /> Safety boundary: only the {readiness.system.count} System strategists are wired into the live engine. Certified/Production custom strategists stay out until shadow testing is designed.</p>
        </div>
      )}

      {/* Business Impact — is the system helping customers succeed? (honest states, no mock data) */}
      <SectionLabel className="mb-2">Business Impact</SectionLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="business-impact">
        <Metric testid="impact-ai-content" label="AI Content Generated" value={missions.length} sub="mission plans produced" icon={FileText} />
        <Metric testid="impact-mi-rate" label="MI-Enhanced Work" value={`${Math.round((analytics?.business?.mi_enhanced_proposal_rate || 0) * 100)}%`} sub="of proposals" icon={BrainCircuit} />
        <AwaitingMetric testid="impact-dna-score" label="Avg Business DNA Score" reason="Available after customer activity" icon={Sparkles} />
        <AwaitingMetric testid="impact-websites" label="Websites Generated" reason="Available after production usage" icon={Globe} />
        <AwaitingMetric testid="impact-success-rate" label="Mission Success Rate" reason="Not enough mission history" icon={Target} />
        <AwaitingMetric testid="impact-satisfaction" label="Customer Satisfaction" reason="Awaiting live data" icon={CheckCircle2} />
        <AwaitingMetric testid="impact-dna-growth" label="Business DNA Growth" reason="Available after customer activity" icon={ArrowUpRight} />
        <AwaitingMetric testid="impact-revenue" label="Revenue Influenced" reason="Available after production usage" icon={Gauge} />
      </div>

      {/* Recent missions + alerts */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <SectionLabel className="mb-2">Recent Missions</SectionLabel>
          <DataTable testid="dashboard-recent-missions" rows={recent} onRowClick={(m) => setPlanOpen(m)}
            rowTestid={(m) => `recent-mission-${m.id}`}
            empty="No mission plans yet."
            columns={[
              { key: "org", label: "Organization", render: (m) => <span className="text-white">{m.org_name || "—"}</span> },
              { key: "ver", label: "Ver", className: "font-mono text-xs", render: (m) => `v${m.version}` },
              { key: "ind", label: "Industry", render: (m) => titleCase(m.context?.data?.industry || "—") },
              { key: "conf", label: "Confidence", render: (m) => <ConfBar value={m.document?.confidence || 0} /> },
              { key: "prov", label: "Provider", render: (m) => <span className="capitalize">{m.document?.provider_used || "—"}</span> },
            ]} />
        </div>
        <div>
          <SectionLabel className="mb-2">System Alerts</SectionLabel>
          <Panel className="p-4 space-y-3 min-h-[120px]" testid="system-alerts">
            {alerts.length === 0 ? (
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 size={16} /> All systems nominal.</div>
            ) : alerts.map((a, i) => {
              const meta = {
                Critical: [ShieldAlert, "text-red-400", "red"],
                Warning: [TriangleAlert, "text-amber-400", "amber"],
                Opportunity: [Lightbulb, "text-gold", "gold"],
                Information: [Info, "text-slate-400", "slate"],
              }[a.cat] || [Info, "text-slate-400", "slate"];
              const [Icon, color, tone] = meta;
              return (
                <div key={i} className="flex gap-2.5 text-sm" data-testid={`alert-${i}`}>
                  <Icon size={15} className={cx("shrink-0 mt-0.5", color)} />
                  <div className="min-w-0">
                    <Badge tone={tone} testid={`alert-cat-${i}`}>{a.cat}</Badge>
                    <span className="text-slate-300 leading-snug block mt-1">{a.text}</span>
                  </div>
                </div>
              );
            })}
          </Panel>
        </div>
      </div>

      {/* Recent Enterprise Activity — Operational Intelligence (centralized audit) */}
      <div className="mb-6" data-testid="dashboard-activity">
        <Panel className="p-5">
          <ActivityTimeline title="Recent Enterprise Activity"
            subtitle="Live operational feed · powered by the centralized Audit System"
            mode="global" showFilters limit={12} testid="dashboard-activity-timeline" />
        </Panel>
      </div>

      {/* Quick access */}
      <SectionLabel className="mb-2">Quick Access</SectionLabel>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <button key={t.label} onClick={() => navigate(t.to)} data-testid={`quick-${t.label.toLowerCase()}`}
            className="group bg-navy-800 border border-white/10 hover:border-gold/40 rounded-sm p-5 text-left transition-colors">
            <div className="flex items-center justify-between mb-3"><t.icon size={18} className="text-gold" /><ArrowUpRight size={15} className="text-slate-600 group-hover:text-gold transition-colors" /></div>
            <div className="text-white text-sm font-medium">{t.label}</div>
          </button>
        ))}
      </div>

      {planOpen && (
        <Drawer open={!!planOpen} onClose={() => setPlanOpen(null)} testid="plan-timeline-drawer"
          title={planOpen.org_name || "Mission Plan"} subtitle={`v${planOpen.version} · Mission Timeline`}>
          <DBlock title="Overview">
            <div className="space-y-1.5">
              <DKV label="Client" value={planOpen.client_email || planOpen.owner_id || "—"} />
              <DKV label="Project" value={planOpen.project_id || "—"} />
              <DKV label="Industry" value={titleCase(planOpen.context?.data?.industry || "—")} />
              <DKV label="Provider / Model" value={planOpen.document?.provider_used || "—"} />
              <DKV label="Confidence" value={`${Math.round((planOpen.document?.confidence || 0) * 100)}%`} />
              <DKV label="Created" value={planOpen.created_at ? new Date(planOpen.created_at).toLocaleString() : "—"} />
            </div>
          </DBlock>
          {planOpen.document?.mission_summary && (
            <DBlock title="Business DNA / Mission"><p className="text-slate-300 text-sm leading-relaxed">{planOpen.document.mission_summary}</p></DBlock>
          )}
          <DList title="Recommended Services" items={planOpen.document?.recommended_services} />
          <DList title="Strategists Consulted" items={planOpen.document?.strategists_consulted} />
          <DBlock title="Timeline">
            <ActivityTimeline mode="entity" params={{ entity_type: "mission_plan", entity_id: planOpen.id }}
              limit={15} compact emptyText="No linked activity recorded for this plan yet." testid="plan-activity-timeline" />
          </DBlock>
        </Drawer>
      )}
    </StudioPage>
  );
}
